import React, { useState, useRef } from 'react';
import {
  RadioButtonGroupInput,
  SaveButton,
  SelectInput,
  useNotify
} from 'react-admin';
import {
  Grid,
  Typography,
  makeStyles,
  Dialog,
  DialogContent,
  InputAdornment,
  TextField
} from '@material-ui/core/';
import { FormSpy } from 'react-final-form';

import Button from '@material-ui/core/Button';
import DateInput from '../../core/components/forked/input/DateInput';
import { isValidDate, formatDate } from '../../core/utils';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitleWithCloseButton from '../../core/components/DialogTitleWithCloseButton';
import moment from 'moment';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import LightTooltip from '../../core/components/LightTooltip';
import FundInvestorApi from '../../core/apis/FundInvestorApi';
import NumberFormat from 'react-number-format';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import UdfTab from '../../core/components/tab/UdfTab';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';
import TagInput from '../../core/components/inputs/TagInput';
import omit from 'lodash/omit';
import FundInvestorTagsAssociationsApi from '../../core/apis/FundInvestorTagsAssociationsApi';

const fundInvestorRelationshipStyles = makeStyles({
  root: {
    width: '120px'
  },
  relationshipName: {
    wordBreak: 'break-word'
  },
  relationshipLabel: {
    color: 'grey'
  },
  fieldLabelMargin: {
    marginTop: '-15px'
  },
  helpIcon: {
    color: '#757575',
    paddingLeft: '6px'
  },
  helpIconWidthMargin: {
    color: '#757575',
    paddingLeft: '6px',
    paddingTop: '6px'
  },
  sectionHeaderStyle: {
    display: 'flex',
    marginTop: '15px'
  },
  containerStyle: {
    display: 'block'
  },
  flexStyle: {
    display: 'flex'
  },
  simpleForm: {
    '& > div': {
      '& > div:first-of-type': {
        height: '0'
      }
    }
  },
  adornmentStyle: {
    paddingLeft: '5px'
  },
  label: {
    color: 'grey',
    fontSize: 18,
    fontWeight: 400
  }
});

const InvestorFundRelationshipDialog = props => {
  const {
    handleClose,
    fundInvestor,
    hasWriteFundInvestorPermission,
    hasEditFundInvestorTagPermission,
    openDialog,
    freezeEnabled
  } = props;
  const classes = fundInvestorRelationshipStyles();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const [currentForm, setCurrentForm] = useState(undefined);
  const [additionalFieldsChanged, setAdditionalFieldsChanged] = useState(false);
  const [formFieldsChanged, setFormFieldsChanged] = useState(false);
  const [udfFieldChanged, setUdfFieldChanged] = useState(false);
  const [udfFieldsInvalid, setUdfFieldsInvalid] = useState(false);
  const [hasUdfs, setHasUdfs] = useState(false);

  const [formFieldsInvalid, setFormFieldsInvalid] = useState(true);
  const [formsumbitting, setFormSumbitting] = useState(false);
  const udftabRef = useRef();

  const handleSubmit = async () => {
    setFormSumbitting(true);
    const fundInvestorUpdated = currentForm.getState().values.fundInvestor;
    const fundInvestorStringify = JSON.stringify(
      omit(fundInvestor, 'fundInvestorTags')
    );
    const fundInvestorUpdatedStringify = JSON.stringify(
      omit(fundInvestorUpdated, 'fundInvestorTags')
    );
    let hasError = false;
    let hasModifications = false;

    function filterAndMapTags(tagList, updatedTagList, fundInvestor) {
      return Object.keys(tagList)
        .filter(tagId => !Object.keys(updatedTagList).includes(tagId))
        .map(fiTag => ({
          fundInvestorTagId: fiTag,
          fundInvestor: {
            fundId: fundInvestor.fundId,
            investorId: fundInvestor.investorId
          }
        }));
    }

    const tagAssociationsToAdd = filterAndMapTags(
      fundInvestorUpdated.fundInvestorTags,
      fundInvestor.fundInvestorTags,
      fundInvestor
    );

    const tagAssociationsToRemove = filterAndMapTags(
      fundInvestor.fundInvestorTags,
      fundInvestorUpdated.fundInvestorTags,
      fundInvestor
    );

    // If the form values of the fund-investor details are different from the existing record, we update it.
    if (fundInvestorStringify !== fundInvestorUpdatedStringify) {
      const data = {
        startDate: fundInvestorUpdated.startDate,
        endDate: fundInvestorUpdated.endDate,
        status: fundInvestorUpdated.status,
        commitment: fundInvestorUpdated.totalCommitment
          ? fundInvestorUpdated.totalCommitment
          : null,
        placementFee: fundInvestorUpdated.placementFee
          ? fundInvestorUpdated.placementFee
          : null,
        managementFee: fundInvestorUpdated.managementFee
          ? fundInvestorUpdated.managementFee
          : null,
        commitmentPrecision: fundInvestorUpdated.commitmentPrecision,
        lpSharingPercent: fundInvestorUpdated.lpSharingPercent
          ? fundInvestorUpdated.lpSharingPercent
          : null
      };

      try {
        await FundInvestorApi.update(
          fundInvestorUpdated.fundId,
          fundInvestorUpdated.investorId,
          data
        );
        notify('ra.notification.custom_updated_message');
      } catch (e) {
        notifyServerError(e);
      }
    }

    if (tagAssociationsToAdd.length > 0) {
      hasModifications = true;
      FundInvestorTagsAssociationsApi.create(tagAssociationsToAdd).catch(e => {
        hasError = true;
        notifyServerError(e);
      });
    }

    if (tagAssociationsToRemove.length > 0) {
      hasModifications = true;
      FundInvestorTagsAssociationsApi.delete(tagAssociationsToRemove).catch(
        e => {
          hasError = true;
          notifyServerError(e);
        }
      );
    }

    if (!hasError && hasModifications) {
      notify('ra.notification.custom_updated_message');
    }
    udftabRef.current.saveUdfs(handleClose);
    setFormSumbitting(false);
  };

  const validateFundInvestorDates = values => {
    const errors = { fundInvestor: { profileData: {} } };
    if (values.fundInvestor.startDate && values.fundInvestor.endDate) {
      if (
        moment(values.fundInvestor.startDate).startOf('day') >=
        moment(values.fundInvestor.endDate).startOf('day')
      ) {
        errors.fundInvestor.endDate = 'End Date must be after Start Date';
      }
    }
    return errors;
  };

  const validatePercentageFields = values => {
    if (values.value) {
      return values.value >= 0 && values.value <= 100;
    } else return true;
  };

  const onAdditionalTextFieldChange = (field, values) => {
    const { value } = values;
    currentForm.change(field, value);

    //check if any of fileds from custom NumberFormat fields are changed
    const currentValue = currentForm.getState().values.fundInvestor;
    const initialValue = currentForm.getState().initialValues.fundInvestor;
    const additionalFieldsList = [
      'totalCommitment',
      'managementFee',
      'lpSharingPercent',
      'placementFee'
    ];
    let additionalFeldsChanged = false;
    additionalFeldsChanged = additionalFieldsList.some(field => {
      return (currentValue[field] || '') !== (initialValue[field] || '');
    });
    setAdditionalFieldsChanged(additionalFeldsChanged);
  };

  return (
    <Dialog
      fullWidth
      open={openDialog}
      aria-label={`Edit Fund-Investor Relationship`}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleClose}>
        Edit Fund-Investor Relationship
      </DialogTitleWithCloseButton>
      <DialogContent>
        <>
          <CustomSimpleForm
            toolbar={null}
            className={classes.simpleForm}
            initialValues={{
              fundInvestor: fundInvestor
            }}
            validate={values => validateFundInvestorDates(values)}
          >
            <FormSpy
              subscription={{ pristine: true, valid: true }}
              onChange={props => {
                setFormFieldsChanged(!props.pristine);
                setFormFieldsInvalid(!props.valid);
              }}
            />
            <FormSpy subscription={{ pristine: true, values: true }}>
              {formprops => {
                if (currentForm === undefined) {
                  setCurrentForm(formprops.form);
                }
                const {
                  commitmentPrecision,
                  fundCurrency
                } = formprops.form.getState().values.fundInvestor;
                return (
                  <>
                    <Typography variant="h6">Relationship Details</Typography>
                    <Grid container className={classes.containerStyle}>
                      <Typography className={classes.relationshipLabel}>
                        Relationship Status
                      </Typography>
                      <div className={classes.fieldLabelMargin}>
                        <RadioButtonGroupInput
                          label=""
                          source="fundInvestor.status"
                          choices={[
                            { id: 'ACTIVE', name: 'Active' },
                            { id: 'INACTIVE', name: 'Inactive' }
                          ]}
                          disabled={!hasWriteFundInvestorPermission}
                          helperText={false}
                        />
                      </div>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <Typography className={classes.relationshipLabel}>
                          Fund Name
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <Typography className={classes.relationshipLabel}>
                          Investor Name
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <div className={classes.relationshipName}>
                          {fundInvestor.fundName}
                        </div>
                      </Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <div className={classes.relationshipName}>
                          {fundInvestor.investorName}
                        </div>
                      </Grid>
                    </Grid>
                    <TagInput
                      id="tagInput-fundinvestor"
                      label=""
                      source="fundInvestor.fundInvestorTags"
                      resource="fundinvestortags"
                      hasEditTagPermission={hasEditFundInvestorTagPermission}
                    />
                    <div className={classes.sectionHeaderStyle}>
                      <Typography variant="h6">Commitment and Fees</Typography>
                    </div>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <div className={classes.flexStyle}>
                          <Typography className={classes.relationshipLabel}>
                            Decimal Places
                          </Typography>
                          <LightTooltip
                            placement="bottom"
                            title="Affects input fields for Commitment and Placement Fee"
                          >
                            <HelpOutlineIcon className={classes.helpIcon} />
                          </LightTooltip>
                        </div>
                      </Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6}></Grid>
                      <Grid item xs={3} sm={3} md={3} lg={3}>
                        <div className={classes.fieldLabelMargin}>
                          <SelectInput
                            label=""
                            source="fundInvestor.commitmentPrecision"
                            choices={[
                              { id: 0, name: '0' },
                              { id: 1, name: '1' },
                              { id: 2, name: '2' }
                            ]}
                            allowEmpty
                            variant="standard"
                            helperText={false}
                            optionValue="id"
                            InputProps={{
                              classes: { root: classes.root }
                            }}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={9} sm={9} md={9} lg={9}></Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <NumberFormat
                          label="Commitment"
                          variant="standard"
                          defaultValue={fundInvestor.totalCommitment}
                          prefix={''}
                          decimalScale={
                            commitmentPrecision === 0
                              ? 0
                              : commitmentPrecision || 2
                          }
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale={true}
                          allowNegative={false}
                          customInput={TextField}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                className={classes.adornmentStyle}
                              >
                                <Typography
                                  className={classes.relationshipLabel}
                                >
                                  {fundCurrency}
                                </Typography>
                              </InputAdornment>
                            )
                          }}
                          onValueChange={values => {
                            onAdditionalTextFieldChange(
                              'fundInvestor.totalCommitment',
                              values
                            );
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <NumberFormat
                          label="Management Fee"
                          variant="standard"
                          defaultValue={fundInvestor.managementFee}
                          prefix={''}
                          decimalScale={2}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale={true}
                          allowNegative={false}
                          customInput={TextField}
                          isAllowed={values => validatePercentageFields(values)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="start">
                                <Typography
                                  className={classes.relationshipLabel}
                                >
                                  %
                                </Typography>
                              </InputAdornment>
                            )
                          }}
                          onValueChange={values => {
                            onAdditionalTextFieldChange(
                              'fundInvestor.managementFee',
                              values
                            );
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <NumberFormat
                          label="LP Sharing"
                          variant="standard"
                          defaultValue={fundInvestor.lpSharingPercent}
                          prefix={''}
                          decimalScale={6}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale={true}
                          allowNegative={false}
                          customInput={TextField}
                          isAllowed={values => validatePercentageFields(values)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="start">
                                <Typography
                                  className={classes.relationshipLabel}
                                >
                                  %
                                </Typography>
                              </InputAdornment>
                            )
                          }}
                          onValueChange={values => {
                            onAdditionalTextFieldChange(
                              'fundInvestor.lpSharingPercent',
                              values
                            );
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6}>
                        <NumberFormat
                          label="Placement Fee"
                          variant="standard"
                          defaultValue={fundInvestor.placementFee}
                          prefix={''}
                          decimalScale={
                            commitmentPrecision === 0
                              ? 0
                              : commitmentPrecision || 2
                          }
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale={true}
                          allowNegative={false}
                          customInput={TextField}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                className={classes.adornmentStyle}
                              >
                                <Typography
                                  className={classes.relationshipLabel}
                                >
                                  {fundCurrency}
                                </Typography>
                              </InputAdornment>
                            )
                          }}
                          onValueChange={values => {
                            onAdditionalTextFieldChange(
                              'fundInvestor.placementFee',
                              values
                            );
                          }}
                        />
                      </Grid>
                    </Grid>
                    {freezeEnabled && (
                      <>
                        <div className={classes.sectionHeaderStyle}>
                          <Typography variant="h6">Investor Freeze</Typography>
                          <LightTooltip
                            placement="bottom"
                            title="Limit Investor's access to fund level documents for a specific time period"
                          >
                            <HelpOutlineIcon
                              className={classes.helpIconWidthMargin}
                            />
                          </LightTooltip>
                        </div>
                        <Grid container spacing={2}>
                          <>
                            <Grid item xs={6} sm={6} md={6} lg={6}>
                              <DateInput
                                source="fundInvestor.startDate"
                                label="Investor Freeze Start Date"
                                variant="standard"
                                validate={[isValidDate]}
                                customParseValue={formatDate}
                                disabled={!hasWriteFundInvestorPermission}
                              />
                            </Grid>
                            <Grid item xs={6} sm={6} md={6} lg={6}>
                              <DateInput
                                source="fundInvestor.endDate"
                                label="Investor Freeze End Date"
                                variant="standard"
                                validate={[isValidDate]}
                                customParseValue={formatDate}
                                disabled={!hasWriteFundInvestorPermission}
                              />
                            </Grid>
                          </>
                        </Grid>
                      </>
                    )}
                  </>
                );
              }}
            </FormSpy>
          </CustomSimpleForm>
          <>
            {hasUdfs && (
              <div className={classes.sectionHeaderStyle}>
                <Typography variant="h6">Properties</Typography>
              </div>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <UdfTab
                  ref={udftabRef}
                  id={[fundInvestor.investorId, fundInvestor.fundId]}
                  queryId={['investorId', 'fundId']}
                  type="fundInvestor"
                  hideCommands={true}
                  onChangeUDFs={(dirty, valid) => {
                    setUdfFieldChanged(dirty);
                    setUdfFieldsInvalid(!valid);
                  }}
                  setHasUdfs={setHasUdfs}
                  //hasEditPermission={hasEditPermission}
                />
              </Grid>
            </Grid>
          </>
        </>
      </DialogContent>
      <DialogActions>
        <SaveButton
          saving={formsumbitting}
          handleSubmitWithRedirect={handleSubmit}
          label="Save"
          icon={<></>}
          disabled={
            formFieldsInvalid ||
            udfFieldsInvalid ||
            !(formFieldsChanged || additionalFieldsChanged || udfFieldChanged)
          }
        />
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
          disabled={formsumbitting}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvestorFundRelationshipDialog;
